import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { classNames } from 'utils/utils'
import { Link } from 'react-router-dom'
import { Input } from 'antd'
import {
  Icon,
  HoverableInfoIcon,
  ConfirmationOverlay,
} from 'components/patterns'
import LeadBadge from 'components/LeadBadge'
import styled from '@emotion/styled'

import strings from 'utils/strings'
import { useDispatch, useSelector } from 'react-redux'
import { setSelectedIdWithDrawer } from 'actions/navigator'
import {
  clearFavoriteAddedNotification,
  clearFavoriteRemovedNotification,
  clearFavoriteUpdatedNotification,
  clearFavoriteErrorNotification,
} from 'actions/user'

const DrawerWrapper = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  flex-direction: column;
  background: #fff;
`

const DrawerHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 20px 16px 20px;
  background: #fff;
  flex-shrink: 0;
`

const DrawerContent = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 0 20px 20px 20px;
`

const DrawerTitle = styled.span`
  color: rgba(7, 7, 15, 0.87);
  font-family: Arial;
  font-size: 18px;
  font-style: normal;
  font-weight: 700;
  line-height: 24px;
  display: flex;
  align-items: center;
  gap: 7px;
`

const FavoriteCardWrapper = styled.div`
  position: relative;
  margin-bottom: 8px;
`

const FavoriteCard = styled.div`
  position: relative;
  display: flex;
  width: 385px;
  height: 80px;
  padding: 16px;
  align-items: stretch;
  gap: 16px;
  border-radius: 8px;
  border: 1px solid #e3e5e8;
  background: #fff;
  box-shadow: 0px 1px 4px 0px rgba(7, 7, 15, 0.1);
  box-sizing: border-box;
`

const CardContent = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  justify-content: space-between;
  align-items: stretch;
  height: 100%;
`

const CardLeftContent = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
`

const CardRightContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  flex-shrink: 0;
  height: 100%;
`

const ButtonRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: auto;
`

const SerialNumber = styled.div`
  margin-bottom: 4px;
  display: flex;
  align-items: center;
  gap: 4px;
  a {
    color: #0051c4;
    font-family: Arial;
    font-size: 16px;
    font-style: normal;
    font-weight: 400;
    line-height: 18px;
    text-decoration: none;
    &:hover {
      text-decoration: underline;
    }
  }
`

const StyledLeadBadge = styled(LeadBadge)``

const VehicleModel = styled.div`
  color: rgba(7, 7, 15, 0.4);
  text-align: right;
  white-space: nowrap;
  font-family: Arial;
  font-size: 10px;
  font-style: normal;
  font-weight: 400;
  line-height: 14px;
  letter-spacing: 0.2px;
`

const CustomDescription = styled.div`
  font-size: 12px;
  color: rgba(7, 7, 15, 0.7);
  margin-top: 4px;
  white-space: nowrap;
`

const NoCustomDescription = styled.div`
  color: rgba(7, 7, 15, 0.4);
  font-family: Arial;
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: 16px;
  margin-top: 4px;
  white-space: nowrap;
`

const DescriptionRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  position: absolute;
  left: 16px;
  right: 16px;
  bottom: 16px;
  top: auto;
  background: #fff;
`

const DescriptionEditButton = styled.button`
  display: flex;
  height: 22px;
  padding: 0 8px;
  align-items: center;
  gap: 4px;
  border-radius: 3px;
  border: 1px solid #0b2e6b;
  background: #fff;
  cursor: pointer;
  font-family: Arial;
  font-size: 12px;
  font-weight: 400;
  line-height: 20px;
  color: #0b2e6b;
  box-sizing: border-box;
  &:hover {
    border-color: #1e7aff;
    color: #1e7aff;
  }
`

const DescriptionInput = styled(Input)`
  display: flex;
  flex: 1;
  height: 24px;
  justify-content: center;
  align-items: center;
  font-family: Arial;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 18px;
  &::placeholder {
    color: rgba(7, 7, 15, 0.4);
  }
`

const SaveButton = styled.button`
  display: flex;
  height: 22px;
  padding: 0 8px;
  align-items: center;
  gap: 4px;
  border-radius: 3px;
  border: 1px solid #0b2e6b;
  background: #fff;
  cursor: pointer;
  font-family: Arial;
  font-size: 12px;
  font-weight: 400;
  line-height: 20px;
  color: #0b2e6b;
  box-sizing: border-box;
  &:hover {
    border-color: #1e7aff;
    color: #1e7aff;
  }
`

const MAX_DESCRIPTION_LENGTH = 40
const MAX_MODEL_LENGTH = 35

const truncateText = (text, maxLength) => {
  if (!text || text.length <= maxLength) return text
  return `${text.slice(0, maxLength)}...`
}

const RemoveButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  color: rgba(7, 7, 15, 0.4);
  &:hover {
    color: rgba(7, 7, 15, 0.6);
  }
`

const NoVehiclesContainer = styled.div`
  display: flex;
  height: 100%;
  border: 1px solid #e7e9ea;
  border-radius: 3px;
  justify-content: center;
  align-items: center;
`

const NoVehiclesText = styled.div`
  color: #afb2b4;
  margin: 50px;
  text-align: center;
`

const SuccessNotification = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  background: rgba(48, 166, 33, 1);
  border-radius: 4px;
  margin-top: 16px;
  margin-bottom: 16px;
`

const SuccessNotificationContent = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: #fff;
  font-family: Arial;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 20px;
`

const SuccessNotificationDismiss = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  color: #fff;
  &:hover {
    opacity: 0.8;
  }
`

const ErrorNotification = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  background: #b71c1c;
  border-radius: 4px;
  margin-top: 16px;
  margin-bottom: 16px;
`

const ErrorNotificationContent = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: #fff;
  font-family: Arial;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 20px;
`

const ErrorNotificationDismiss = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  color: #fff;
  &:hover {
    opacity: 0.8;
  }
`

const FavoriteVehiclesDrawer = ({
  className,
  favoriteVehicles,
  updateVehicleFavorite,
  removeVehicleFavorite,
}) => {
  const dispatch = useDispatch()
  const showAddedNotification = useSelector((state) =>
    state.getIn(['user', 'showFavoriteAddedNotification'])
  )
  const showRemovedNotification = useSelector((state) =>
    state.getIn(['user', 'showFavoriteRemovedNotification'])
  )
  const showUpdatedNotification = useSelector((state) =>
    state.getIn(['user', 'showFavoriteUpdatedNotification'])
  )
  const showErrorNotification = useSelector((state) =>
    state.getIn(['user', 'showFavoriteErrorNotification'])
  )
  const [editingSerial, setEditingSerial] = useState(null)
  const [editingDescription, setEditingDescription] = useState('')
  const [confirmingRemoveSerial, setConfirmingRemoveSerial] = useState(null)

  // Clear all notifications when the drawer unmounts (closes)
  useEffect(() => {
    return () => {
      dispatch(clearFavoriteAddedNotification())
      dispatch(clearFavoriteRemovedNotification())
      dispatch(clearFavoriteUpdatedNotification())
      dispatch(clearFavoriteErrorNotification())
    }
  }, [dispatch])

  const handleDismissAddedNotification = () => {
    dispatch(clearFavoriteAddedNotification())
  }

  const handleDismissRemovedNotification = () => {
    dispatch(clearFavoriteRemovedNotification())
  }

  const handleDismissUpdatedNotification = () => {
    dispatch(clearFavoriteUpdatedNotification())
  }

  const handleDismissErrorNotification = () => {
    dispatch(clearFavoriteErrorNotification())
  }

  const handleStartEditDescription = (serialNumber, currentDescription) => {
    setEditingSerial(serialNumber)
    setEditingDescription(currentDescription || '')
  }

  const handleSaveDescription = (serialNumber) => {
    if (updateVehicleFavorite) {
      updateVehicleFavorite(serialNumber, editingDescription)
    }
    setEditingSerial(null)
    setEditingDescription('')
  }

  const handleCancelEditDescription = () => {
    setEditingSerial(null)
    setEditingDescription('')
  }

  const handleRemoveClick = (serialNumber) => {
    setConfirmingRemoveSerial(serialNumber)
  }

  const handleConfirmRemove = (serialNumber) => {
    if (removeVehicleFavorite) {
      removeVehicleFavorite(serialNumber)
    }
    setConfirmingRemoveSerial(null)
  }

  const handleCancelRemove = () => {
    setConfirmingRemoveSerial(null)
  }

  const renderFavoriteCard = (vehicle) => {
    const { serialNumber, uri, isLeadSerial, vehicleModel, customDescription } =
      vehicle
    const isConfirmingRemove = confirmingRemoveSerial === serialNumber

    return (
      <FavoriteCardWrapper key={serialNumber}>
        <FavoriteCard>
          <CardContent>
            <CardLeftContent>
              <SerialNumber>
                {uri ? (
                  <Link
                    to={uri}
                    onClick={() => dispatch(setSelectedIdWithDrawer(null))}
                  >
                    {serialNumber}
                  </Link>
                ) : (
                  serialNumber
                )}
                {isLeadSerial && <StyledLeadBadge />}
              </SerialNumber>
              {editingSerial !== serialNumber && (
                <>
                  {customDescription ? (
                    <CustomDescription>{customDescription}</CustomDescription>
                  ) : (
                    <NoCustomDescription>
                      {strings.no_custom_description}
                    </NoCustomDescription>
                  )}
                </>
              )}
            </CardLeftContent>
            <CardRightContent>
              {vehicleModel && (
                <VehicleModel title={vehicleModel}>
                  {truncateText(vehicleModel, MAX_MODEL_LENGTH)}
                </VehicleModel>
              )}
              {editingSerial !== serialNumber && (
                <ButtonRow>
                  <DescriptionEditButton
                    onClick={() =>
                      handleStartEditDescription(
                        serialNumber,
                        customDescription
                      )
                    }
                  >
                    Edit
                  </DescriptionEditButton>
                  <RemoveButton onClick={() => handleRemoveClick(serialNumber)}>
                    <Icon
                      icon="plus-circle"
                      style={{
                        color: '#4f5764',
                        fontSize: '20px',
                        transform: 'rotate(45deg)',
                      }}
                      fill="outlined"
                      hoverFill="filled"
                    />
                  </RemoveButton>
                </ButtonRow>
              )}
            </CardRightContent>
          </CardContent>
          {editingSerial === serialNumber && (
            <DescriptionRow>
              <DescriptionInput
                value={editingDescription}
                onChange={(e) => setEditingDescription(e.target.value)}
                onPressEnter={() => handleSaveDescription(serialNumber)}
                onKeyDown={(e) => {
                  if (e.key === 'Escape') {
                    e.stopPropagation()
                    handleCancelEditDescription()
                  }
                }}
                maxLength={MAX_DESCRIPTION_LENGTH}
                placeholder={strings.custom_description_placeholder}
                autoFocus
              />
              <SaveButton onClick={() => handleSaveDescription(serialNumber)}>
                Save
              </SaveButton>
            </DescriptionRow>
          )}
        </FavoriteCard>
        {isConfirmingRemove && (
          <ConfirmationOverlay
            message={strings.remove_favorite_confirmation}
            cancelLabel={strings.no_keep_it}
            confirmLabel={strings.yes_remove_it}
            onCancel={handleCancelRemove}
            onConfirm={() => handleConfirmRemove(serialNumber)}
          />
        )}
      </FavoriteCardWrapper>
    )
  }

  return (
    <DrawerWrapper
      className={classNames(className, 'favorite-vehicles-drawer')}
    >
      <DrawerHeader>
        <DrawerTitle>
          <Icon icon="FaStar" color="#FFC627" />
          {strings.favorite_vehicles}
          <HoverableInfoIcon
            tooltip={strings.favorite_vehicles_tooltip}
            tooltipPosition="bottom"
            overlayStyle={{
              color: '#fff',
              fontFamily: 'Arial',
              fontSize: '14px',
              fontStyle: 'normal',
              fontWeight: 400,
              lineHeight: '18px',
              maxWidth: '350px',
              whiteSpace: 'pre-line',
            }}
          />
        </DrawerTitle>
      </DrawerHeader>
      <DrawerContent>
        {showAddedNotification && (
          <SuccessNotification>
            <SuccessNotificationContent>
              <Icon icon="check-mark-only" fill="whitePrimary" />
              <span>
                <strong>You got it!</strong> Favorite vehicle added.
              </span>
            </SuccessNotificationContent>
            <SuccessNotificationDismiss
              onClick={handleDismissAddedNotification}
            >
              <Icon icon="close" style={{ color: '#fff', fontSize: 20 }} />
            </SuccessNotificationDismiss>
          </SuccessNotification>
        )}
        {showRemovedNotification && (
          <SuccessNotification>
            <SuccessNotificationContent>
              <Icon icon="check-mark-only" fill="whitePrimary" />
              <span>
                <strong>You got it!</strong> Favorite vehicle removed.
              </span>
            </SuccessNotificationContent>
            <SuccessNotificationDismiss
              onClick={handleDismissRemovedNotification}
            >
              <Icon icon="close" style={{ color: '#fff', fontSize: 20 }} />
            </SuccessNotificationDismiss>
          </SuccessNotification>
        )}
        {showUpdatedNotification && (
          <SuccessNotification>
            <SuccessNotificationContent>
              <Icon icon="check-mark-only" fill="whitePrimary" />
              <span>
                <strong>You got it!</strong> Favorite vehicle updated.
              </span>
            </SuccessNotificationContent>
            <SuccessNotificationDismiss
              onClick={handleDismissUpdatedNotification}
            >
              <Icon icon="close" style={{ color: '#fff', fontSize: 20 }} />
            </SuccessNotificationDismiss>
          </SuccessNotification>
        )}
        {showErrorNotification && (
          <ErrorNotification>
            <ErrorNotificationContent>
              <Icon
                icon="alert_banner_error_diamond_cross"
                fill="whitePrimary"
                width={20}
                height={20}
              />
              <span>
                <strong>Whoops!</strong> Something went wrong.
              </span>
            </ErrorNotificationContent>
            <ErrorNotificationDismiss onClick={handleDismissErrorNotification}>
              <Icon icon="close" style={{ color: '#fff', fontSize: 20 }} />
            </ErrorNotificationDismiss>
          </ErrorNotification>
        )}
        {favoriteVehicles.length === 0 ? (
          <NoVehiclesContainer>
            <NoVehiclesText>{strings.no_favorite_vehicles}</NoVehiclesText>
          </NoVehiclesContainer>
        ) : (
          favoriteVehicles.map((vehicle) => renderFavoriteCard(vehicle))
        )}
      </DrawerContent>
    </DrawerWrapper>
  )
}

FavoriteVehiclesDrawer.defaultProps = {
  favoriteVehicles: [],
}

FavoriteVehiclesDrawer.propTypes = {
  className: PropTypes.string,
  favoriteVehicles: PropTypes.array,
  updateVehicleFavorite: PropTypes.func,
  removeVehicleFavorite: PropTypes.func,
}

export default FavoriteVehiclesDrawer
